import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import BookCard from "@/components/BookCard";
import { applyPageSeo } from "@/lib/seo";
import { Phone, Truck, ShieldCheck, Sparkles, ArrowRight, BookOpen } from "lucide-react";

const CATEGORY_TILES = [
  { key: "History", label: "History", span: "md:col-span-6 lg:col-span-7", h: "h-56 md:h-72", img: "https://images.unsplash.com/photo-1461360370896-922624d12aa1?w=1200&q=80" },
  { key: "Art and Archaeology", label: "Art & Archaeology", span: "md:col-span-3 lg:col-span-5", h: "h-56 md:h-72", img: "https://images.unsplash.com/photo-1502005229762-cf1b2da7c5d6?w=1200&q=80" },
  { key: "Civil Engineering", label: "Civil Engineering", span: "md:col-span-3 lg:col-span-4", h: "h-48", img: "https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=1200&q=80" },
  { key: "Mechanical Engineering", label: "Mechanical Engineering", span: "md:col-span-3 lg:col-span-4", h: "h-48", img: "https://images.unsplash.com/photo-1607582544142-8bfa1f39caa7?w=1200&q=80" },
  { key: "Children Books", label: "Children's Books", span: "md:col-span-3 lg:col-span-4", h: "h-48", img: "https://images.unsplash.com/photo-1512820790803-83ca734da794?w=1200&q=80" },
  { key: "Biography and Autobiography", label: "Biography", span: "md:col-span-3 lg:col-span-4", h: "h-44", img: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=1200&q=80" },
  { key: "Philosophy and Religion", label: "Philosophy & Religion", span: "md:col-span-3 lg:col-span-4", h: "h-44", img: "https://images.unsplash.com/photo-1499209974431-9dddcece7f88?w=1200&q=80" },
  { key: "Old and Rare Books", label: "Old & Rare Books", span: "md:col-span-3 lg:col-span-4", h: "h-44", img: "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=1200&q=80" },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [facets, setFacets] = useState({ publishers: [] });

  useEffect(() => {
    applyPageSeo({
      title: "GAINA THE PRELUDE — Buy Scholarly & Rare Books Online",
      description: "A Curated Books Distributor for Every Kind of Reader. Buy scholarly, rare, coffee-table, academic, Children, Law & Hindi literature online. Worldwide shipping. Trusted by universities, colleges, museums, research institutions, libraries and corporates.",
      keywords: "Indian bookstore, rare books, scholarly books, coffee table books, children books, law books, art books, history books, buddhism books, GAINA THE PRELUDE, gainabooks, books distributor, library supplier, bulk book orders",
      path: "/",
    });
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/books", { params: { limit: 12, featured: true, sort: "newest" } });
        setFeatured(data.items || []);
      } catch (e) {
        console.error("featured books load failed", e);
      }
      try {
        const { data } = await api.get("/books", { params: { limit: 8, new_arrival: true, sort: "newest" } });
        setNewArrivals(data.items || []);
      } catch (e) { /* silent */ }
      try {
        const { data } = await api.get("/books/facets");
        setFacets(data);
      } catch (e) {
        console.error("facets load failed", e);
      }
    })();
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative border-b border-border overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5 space-y-6">
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-black tracking-tighter leading-[0.9] text-[#ea580c]" data-testid="hero-brand-title">
              GAINA<br />
              <span className="relative inline-block">
                THE PRELUDE
                <span className="absolute -bottom-2 left-0 w-full h-2 bg-[#ea580c]" />
              </span>
            </h1>
            <p className="text-base md:text-lg text-[#52525b] leading-relaxed max-w-xl">
              Discover a curated collection of coffee-table titles, rare finds, academic essentials and everything in between.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/books" data-testid="hero-shop-cta" className="inline-flex items-center justify-center gap-2 bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-7 py-4 transition-colors">
                Shop All Books <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:9953272630" data-testid="hero-call-cta" className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#09090b] text-[#09090b] hover:bg-[#09090b] hover:text-white font-bold px-7 py-4 transition-colors">
                <Phone className="w-4 h-4" /> Call 99532 72630
              </a>
            </div>

            {/* Trusted-by credentials strip */}
            <div className="pt-6 border-t border-border" data-testid="hero-trusted-by">
              <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-[#ea580c] mb-3">
                <ShieldCheck className="w-3.5 h-3.5" /> Trusted by
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-[13px] md:text-sm font-bold text-[#09090b] leading-snug">
                <span>Universities</span>
                <span className="text-[#e4e4e7]">|</span>
                <span>Colleges</span>
                <span className="text-[#e4e4e7]">|</span>
                <span>Museums</span>
                <span className="text-[#e4e4e7]">|</span>
                <span>Research Institutions</span>
                <span className="text-[#e4e4e7]">|</span>
                <span>Libraries</span>
                <span className="text-[#e4e4e7]">|</span>
                <span>Corporates</span>
                <span className="text-[#a1a1aa]">…</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7 relative">
            <div
              className="relative aspect-[5/4] md:aspect-[16/10] lg:aspect-[6/5] border-2 border-[#09090b] overflow-hidden bg-[#09090b]"
              data-testid="hero-image"
            >
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url("https://customer-assets.emergentagent.com/job_priya-book-platform/artifacts/za73e380_Screenshot_2026-07-06-11-05-16-02_f9ee0578fe1cc94de7482bd41accb329.webp")`,
                  backgroundSize: "140% auto",
                  backgroundPosition: "left 42%",
                  backgroundRepeat: "no-repeat",
                }}
                aria-label="Curated library shelves"
                role="img"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-[#09090b] text-white p-4 flex items-center justify-between">
                <div>
                  <div className="text-[10px] tracking-[0.2em] uppercase text-[#fb923c]">Bulk Orders</div>
                  <div className="font-display font-black text-lg">Best in the Business</div>
                </div>
                <Truck className="w-8 h-8 text-[#fb923c]" strokeWidth={2} />
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="border-y border-border bg-[#f4f4f5] overflow-hidden">
          <div className="animate-marquee whitespace-nowrap py-3 text-sm font-bold tracking-widest text-[#09090b]">
            {["m1", "m2"].map((k) => (
              <span key={k}>
                HISTORY • ART &amp; ARCHAEOLOGY • BIOGRAPHY &amp; AUTOBIOGRAPHY • CIVIL ENGINEERING • MECHANICAL ENGINEERING • AEROSPACE ENGINEERING • HINDI LITERATURE • BUDDHISM • PHILOSOPHY &amp; RELIGION • OLD &amp; RARE BOOKS • SCIENCE &amp; TECHNOLOGY • ECONOMICS •&nbsp;&nbsp;
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* DESTINATION STATEMENT */}
      <section className="border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 space-y-5">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Our Promise</div>
            <h2 className="font-display text-3xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05]">
              A Destination for<br />Every Reader.
            </h2>
            <p className="text-base md:text-lg text-[#52525b] leading-relaxed">
              Books have the power to inspire, transform, and connect us. Our bookstore brings together an exceptional collection of fiction, non-fiction, children's books, biographies, self-help, business, art, travel, and stunning coffee table books — all thoughtfully selected to enrich every bookshelf.
            </p>
            <p className="text-base md:text-lg text-[#52525b] leading-relaxed">
              Find stories you'll love, knowledge you'll treasure, and books you'll proudly display.
            </p>
            <div className="pt-2 inline-block border-t-2 border-[#09090b] pt-4">
              <div className="font-display text-lg md:text-xl font-black tracking-[0.15em] uppercase">
                Where Every Book <span className="text-[#ea580c]">Finds Its Readers</span>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 grid grid-cols-2 gap-3">
            <div className="border-2 border-[#09090b] aspect-[4/5] overflow-hidden">
              <img src="https://images.unsplash.com/photo-1519681393784-d120267933ba?w=800&q=80" alt="Coffee table books" className="w-full h-full object-cover" />
            </div>
            <div className="border-2 border-[#09090b] aspect-[4/5] overflow-hidden mt-8">
              <img src="https://images.pexels.com/photos/1848731/pexels-photo-1848731.jpeg?w=800" alt="Reading" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="flex items-end justify-between mb-8 border-b-2 border-[#09090b] pb-4">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Browse</div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight">By Category</h2>
          </div>
          <Link to="/categories" className="hidden md:inline text-sm font-bold hover:text-[#ea580c]" data-testid="home-view-all-categories">View All →</Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-4" data-testid="category-bento">
          {CATEGORY_TILES.map((t) => (
            <Link
              key={t.key}
              to={`/books?category=${encodeURIComponent(t.key)}`}
              className={`relative overflow-hidden border border-border group ${t.span} ${t.h} bg-black`}
              data-testid={`category-tile-${t.key}`}
            >
              <img src={t.img} alt={t.label} className="absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-110 transition-all duration-500" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="relative h-full flex flex-col justify-end p-5">
                <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#fb923c]">Shop</div>
                <div className="font-display text-2xl md:text-3xl font-black text-white leading-tight">{t.label}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* NEW ARRIVALS (only if admin has flagged any) */}
      {newArrivals.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16" data-testid="new-arrivals-section">
          <div className="flex items-end justify-between mb-8 border-b-2 border-[#3b82f6] pb-4">
            <div>
              <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#3b82f6]">✨ Just In</div>
              <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight">New Arrivals</h2>
            </div>
            <Link to="/books?new_arrival=true" className="text-sm font-bold hover:text-[#3b82f6]" data-testid="new-arrivals-view-all">View All →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="new-arrivals-grid">
            {newArrivals.map((b) => <BookCard key={b.id} book={b} />)}
          </div>
        </section>
      )}

      {/* FEATURED */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 pt-16">
        <div className="flex items-end justify-between mb-8 border-b-2 border-[#09090b] pb-4">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Curated Selection</div>
            <h2 className="font-display text-3xl md:text-5xl font-black tracking-tight">Featured Books</h2>
          </div>
          <Link to="/books" className="text-sm font-bold hover:text-[#ea580c]" data-testid="featured-view-all">View All →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="featured-grid">
          {featured.map((b) => <BookCard key={b.id} book={b} />)}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="border-2 border-[#09090b] bg-[#09090b] text-white p-8 md:p-14 grid md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#fb923c] mb-2">Bulk & Institutional Orders</div>
            <h3 className="font-display text-3xl md:text-4xl font-black leading-tight">Ordering for an Institutional, call us for special pricing.</h3>
          </div>
          <div className="flex flex-col gap-3">
            <a href="tel:9953272630" className="bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-6 py-4 flex items-center justify-center gap-2" data-testid="bulk-call-cta">
              <Phone className="w-4 h-4" /> +91 99532 72630
            </a>
            <a href="mailto:gainatheprelude@gmail.com" className="bg-white text-[#09090b] font-bold px-6 py-4 flex items-center justify-center gap-2">
              Send Enquiry
            </a>
          </div>
        </div>
      </section>

      {/* FEATURE BADGES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Feature icon={<Truck />} title="Pan-India Delivery" desc="Fast dispatch across every state." />
        <Feature icon={<ShieldCheck />} title="100% Genuine" desc="Direct-from-publisher authentic titles." />
      </section>
    </div>
  );
}

function Feature({ icon, title, desc }) {
  return (
    <div className="border border-border p-6 hover:border-[#ea580c] transition-colors">
      <div className="w-10 h-10 bg-[#09090b] text-white flex items-center justify-center mb-4">
        {icon}
      </div>
      <div className="font-display font-black text-lg mb-1">{title}</div>
      <div className="text-sm text-[#52525b]">{desc}</div>
    </div>
  );
}
